export interface IBaseCommand<TResult>
{
}

export type Guid = string;

export interface IEndPairing extends IBaseCommand<string>
{
    code: string;
    salePointId: Guid;
}

export interface ICardReaderView extends IEntityView
{
    readerId: string;
    id: Guid;
    name: string;
}

export interface IEntityView
{
    id: Guid;
    name: string;
}

export interface IListResult<TItem>
{
    items: TItem[];
    total: number;
}

export interface IListPagination
{
    offset: number;
    limit: number;
}

export interface IListSort
{
    property: string;
    direction: SortDirection;
}

export enum SortDirection
{
    Ascending = 1,
    Descending = 2,
}

export interface IListCommand<TItem> extends IBaseCommand<IListResult<TItem>>
{
    pagination: IListPagination;
    sort: IListSort;
}

export interface IListCardReader extends IListCommand<ICardReaderView>
{
    pagination: IListPagination;
    sort: IListSort;
}

export interface IRegisterCardReader extends IBaseCommand<Guid>
{
    name: string;
    readerId: string;
    ipAddress: string;
    network: string;
}

export interface IStartPairing extends IBaseCommand<string>
{
    readerId: string;
}

export interface IWaitForCardRead extends IBaseCommand<string>
{
    readerId: string;
    timeout: number|string;
    paymentToken: string;
}

export enum DeviceKeyType
{
    Auth = 1,
    Pos = 2,
}

export interface IAuthorizeKey extends IBaseCommand<boolean>
{
    fingerprint: string;
    deviceSerial: string;
    type: DeviceKeyType;
}

export interface ICheckDeviceRegistration extends IBaseCommand<boolean>
{
    id: Guid;
    serial: string;
}

export interface IDeviceListView extends IEntityView
{
    lastAccessTime: Date | string;
    type: DeviceType;
    info: IDeviceInfo;
    serial: string;
    id: Guid;
    name: string;
}

export enum DeviceType
{
    Android = 1,
    Browser = 2,
    Service = 3,
}

export interface IDeviceInfo
{
    manufacturer: string;
    brand: string;
    model: string;
    device: string;
    product: string;
    hardware: string;
    board: string;
    bootloader: string;
    fingerprint: string;
    versionSdk: number;
    versionRelease: string;
    versionCodename: string;
    cpuAbi: string;
}

export interface IListDevice extends IListCommand<IDeviceListView>
{
    pagination: IListPagination;
    sort: IListSort;
}

export interface IRegisterDevice extends IBaseCommand<Guid>
{
    serial: string;
    type: DeviceType;
    friendlyName: string;
    info: IDeviceInfo;
    posKeys: string[];
    authKeys: string[];
    fcmToken: string;
}

export interface ISystemVersion
{
    version: string;
    env: string;
    dbName: string;
    dbServer: string;
}

export interface IGetSystemVersion extends IBaseCommand<ISystemVersion>
{
}

export interface ICreateMerchant extends IBaseCommand<Guid>
{
    name: string;
    companyName: string;
    taxCode: string;
    billAddress: IAddressData;
}

export interface IAddressData
{
    city: string;
    postalCode: string;
    streetName: string;
    countryCode: string;
}

export enum MerchantRole
{
    Organizer = 1,
    Seller = 2,
}

export interface ICreateSalePoint extends IBaseCommand<Guid>
{
    saleCircuitId: Guid;
    name: string;
    type: SalePointType;
    address: IAddressData;
    merchantId: Guid;
    merchantName: string;
    merchantEmail: string;
}

export enum SalePointType
{
    Sell = 1,
    Charge = 2,
    All = 3,
    Online = 4,
}

export interface IAddMerchant extends IBaseCommand<Guid>
{
    saleCircuitId: Guid;
    merchantId: Guid;
    merchant: ICreateMerchant;
    role: MerchantRole;
    salePoint: ICreateSalePoint;
}

export interface IMerchantListView extends IEntityView
{
    city: string;
    id: Guid;
    name: string;
}

export interface IListMerchant extends IListCommand<IMerchantListView>
{
    saleCircuitId: Guid;
    ids: Guid[];
    pagination: IListPagination;
    sort: IListSort;
}

export interface ICreateOrderItem
{
    productId: Guid;
    formatId: Guid;
    quantity: number;
    description: string;
    unitAmount: number;
    vatPercentage: number;
    type: OrderItemType;
}

export enum OrderItemType
{
    Product = 1,
    Percentage = 2,
    Amount = 3,
}

export interface IChangeOrder extends IBaseCommand<boolean>
{
    id: Guid;
    salePointId: Guid;
    operatorId: Guid;
    items: ICreateOrderItem[];
}

export enum OrderType
{
    Buy = 0,
    Charge = 1,
    CardRefund = 2,
}

export enum RefundCardReason
{
    Unspecified = 0,
    Withdrawal = 1,
    Extinction = 2,
}

export interface ICreateOrder extends IBaseCommand<Guid>
{
    salePointId: Guid;
    merchantId: Guid;
    operatorId: Guid;
    type: OrderType;
    refundReason: RefundCardReason;
    note: string;
    items: ICreateOrderItem[];
}

export interface ICreatePayment extends IBaseCommand<string>
{
    salePointId: Guid;
    operatorId: Guid;
    orderId: Guid;
    cardReaderId: string;
}

export interface IOrderDetailsView extends IOrderListView
{
    items: IOrderItem[];
    history: IOrderHistory[];
    refund: IOrderRefundView;
    payment: IOrderPaymentView;
    date: Date | string;
    saleCircuit: IEntityView;
    salePoint: IEntityView;
    merchant: IEntityView;
    customer: IEntityView;
    status: OrderStatus;
    type: OrderType;
    paymentMethod: PaymentMethod;
    total: number;
    id: Guid;
    name: string;
}

export interface IOrderItem
{
    productId: Guid;
    formatId: Guid;
    quantity: number;
    description: string;
    unitAmount: number;
    vatPercentage: number;
    type: OrderItemType;
}

export interface IOrderHistory
{
    operatorId: Guid;
    salePointId: Guid;
    deviceId: Guid;
    transactionId: Guid;
    time: Date | string;
    status: OrderStatus;
}

export enum OrderStatus
{
    Created = 0,
    WaitForPayment = 1,
    Paid = 2,
    Cancelled = 3,
    Refunded = 4,
    Changed = 5,
}

export interface IOrderRefundView
{
    time: Date | string;
    reason: RefundOrderReason;
    note: string;
    amount: number;
}

export enum RefundOrderReason
{
    Unspecified = 0,
    WrongAmount = 1,
    ServiceNotProvided = 2,
    CustomerRequest = 3,
    Fraudulent = 4,
    ProductIssue = 5,
}

export interface IOrderPaymentView
{
    time: Date | string;
    method: PaymentMethod;
    cardId: Guid;
    cardCode: string;
}

export enum PaymentMethod
{
    Cash = 1,
    BankCard = 2,
    NfcCard = 4,
    NfcDevice = 8,
    QrCard = 16,
    Unknown = 32,
    Gift = 64,
    BankTransfer = 128,
}

export interface IOrderListView extends IEntityView
{
    date: Date | string;
    saleCircuit: IEntityView;
    salePoint: IEntityView;
    merchant: IEntityView;
    customer: IEntityView;
    status: OrderStatus;
    type: OrderType;
    paymentMethod: PaymentMethod;
    total: number;
    id: Guid;
    name: string;
}

export interface IGetOrder extends IBaseCommand<IOrderDetailsView>
{
    id: Guid;
}

export interface IListOrder extends IListCommand<IOrderListView>
{
    ids: Guid[];
    saleCircuitId: Guid;
    salePointId: Guid;
    merchantId: Guid;
    customerId: Guid;
    paymentCardId: Guid;
    fromDate: Date | string;
    toDate: Date | string;
    status: OrderStatus[];
    type: OrderType[];
    paymentMethod: PaymentMethod[];
    minAmount: number;
    maxAmount: number;
    query: string;
    pagination: IListPagination;
    sort: IListSort;
}

export interface IPayResult
{
    isSuccess: boolean;
    resultCode: PaymentResult;
    cardBalance: number;
    errorMessage: string;
    cardReaderId: string;
}

export enum PaymentResult
{
    Success = 1,
    InvalidCardToken = 10,
    InvalidAmount = 11,
    InvalidPaymentToken = 12,
    InvalidOrderStatus = 13,
    TokenIsBurned = 14,
    CardNotActive = 15,
    CardBlocked = 16,
    WalletBlocked = 17,
    InsufficientCredit = 18,
    InvalidSalePoint = 19,
    WalletExpired = 20,
    InvalidCardSaleCircuit = 21,
    UnknownError = 50,
}

export interface IExternalPayProvider
{
    name: string;
    transactionId: string;
}

export interface IPayOrder extends IBaseCommand<IPayResult>
{
    salePointId: Guid;
    operatorId: Guid;
    orderId: Guid;
    cardToken: string;
    connectionId: string;
    method: PaymentMethod;
    external: IExternalPayProvider;
}

export interface IRefundOrder extends IBaseCommand<boolean>
{
    orderId: Guid;
    salePointId: Guid;
    operatorId: Guid;
    connectionId: string;
    reason: RefundOrderReason;
    amount: number;
    note: string;
}

export interface IAuthorizePayment extends IBaseCommand<string>
{
    amount: number;
    cardReaderId: string;
}

export interface IBlockCardResult
{
    blockCode: number;
}

export interface IBlockCard extends IBaseCommand<IBlockCardResult>
{
    cardId: Guid;
    salePointId: Guid;
}

export interface IEncodeCardCode extends IBaseCommand<string>
{
    cardCode: string;
    paymentToken: string;
}

export interface IPaymentCardInfo
{
    cardId: Guid;
    cardCode: string;
    balance: number;
    refundableBalance: number;
    cardStatus: PaymentCardStatus;
    lastOperationTime: Date | string;
    walletStatus: WalletStatus;
    walletId: Guid;
    saleCircuit: IEntityView;
    userId: Guid;
    isTokenBurned: boolean;
    type: PaymentCardType;
    emissionSalePoint: IEntityView;
    emissionTime: Date | string;
    blockTime: Date | string;
    customer: IOwnerView;
    hasTransactions: boolean;
    expirationTime: Date | string;
}

export enum PaymentCardStatus
{
    NotActive = 0,
    Active = 1,
    Blocked = 2,
}

export enum WalletStatus
{
    Active = 0,
    Closed = 1,
    Blocked = 2,
    Expired = 3,
}

export enum PaymentCardType
{
    Nfc = 1,
    QrCode = 2,
}

export interface IOwnerView extends IEntityView
{
    email: string;
    mobileNumber: string;
    id: Guid;
    name: string;
}

export interface IGetCardInfo extends IBaseCommand<IPaymentCardInfo>
{
    id: Guid;
    cardCode: string;
    checkToken: string;
}

export interface IInitCard extends IBaseCommand<Guid>
{
    salePointId: Guid;
    allowReset: boolean;
    walletId: Guid;
    cardType: PaymentCardType;
    cardCode: string;
    customerEmail: string;
    customerFullName: string;
    customerPhoneNumber: string;
    expirationTime: Date | string;
}

export interface ICardListView extends IEntityView
{
    code: string;
    balance: number;
    walletId: Guid;
    customer: IOwnerView;
    type: PaymentCardType;
    status: PaymentCardStatus;
    emissionTime: Date | string;
    lastOperationTime: Date | string;
    blockTime: Date | string;
    id: Guid;
    name: string;
}

export enum WalletType
{
    Customer = 1,
    Merchant = 2,
    Cash = 3,
    Drop = 4,
}

export enum CardBalanceType
{
    All = 0,
    WithBalance = 1,
    WithoutBalance = 2,
}

export interface IListCard extends IListCommand<ICardListView>
{
    ids: Guid[];
    saleCircuitId: Guid;
    emittedSalePointId: Guid;
    query: string;
    code: string;
    status: PaymentCardStatus;
    type: PaymentCardType;
    walletType: WalletType;
    balanceType: CardBalanceType;
    balanceMin: number;
    balanceMax: number;
    lastOperationFrom: Date | string;
    lastOperationTo: Date | string;
    lastInitFrom: Date | string;
    lastInitTo: Date | string;
    pagination: IListPagination;
    sort: IListSort;
}

export interface IReplaceCard extends IBaseCommand<Guid>
{
    oldCardId: Guid;
    newCardToken: string;
    allowResetNew: boolean;
    salePointId: Guid;
}

export interface IResetCard extends IBaseCommand<boolean>
{
    cardId: Guid;
    salePointId: Guid;
    closeWallet: boolean;
}

export interface IUnblockCard extends IBaseCommand<boolean>
{
    cardId: Guid;
    blockCode: number;
    salePointId: Guid;
}

export interface ICreateCatalog extends IBaseCommand<Guid>
{
    merchantId: Guid;
    name: string;
}

export interface ICreateProductFormat
{
    price: number;
    name: string;
    value: number;
    unit: string;
}

export interface ICreateProduct extends IBaseCommand<Guid>
{
    merchantId: Guid;
    name: string;
    description: string;
    color: string;
    formats: ICreateProductFormat[];
}

export interface IDeleteCatalog extends IBaseCommand<boolean>
{
    id: Guid;
    merchantId: Guid;
}

export interface IDeleteProduct extends IBaseCommand<boolean>
{
    id: Guid;
    merchantId: Guid;
}

export interface ICatalogFullView extends IEntityView
{
    merchantId: Guid;
    root: ICatalogGroupView;
    id: Guid;
    name: string;
}

export interface ICatalogGroupView
{
    name: string;
    icon: string;
    isHidden: boolean;
    products: ICatalogProductView[];
    groups: ICatalogGroupView[];
}

export interface ICatalogProductView
{
    id: Guid;
    name: string;
    description: string;
    color: string;
    status: CatalogProductStatus;
    formats: IProductFormatView[];
}

export enum CatalogProductStatus
{
    None = 0,
}

export interface IProductFormatView
{
    id: Guid;
    price: number;
    name: string;
    value: number;
    unit: string;
}

export interface IGetFullCatalog extends IBaseCommand<ICatalogFullView>
{
    id: Guid;
}

export interface ICatalogListView extends IEntityView
{
    isActive: boolean;
    id: Guid;
    name: string;
}

export interface IListCatalog extends IListCommand<ICatalogListView>
{
    merchantId: Guid;
    pagination: IListPagination;
    sort: IListSort;
}

export interface IProductListView extends IEntityView
{
    description: string;
    color: string;
    formats: IProductFormatView[];
    merchant: IEntityView;
    id: Guid;
    name: string;
}

export interface IListProduct extends IListCommand<IProductListView>
{
    merchantId: Guid;
    saleCircuitId: Guid;
    excludeIds: Guid[];
    ids: Guid[];
    query: string;
    pagination: IListPagination;
    sort: IListSort;
}

export interface ICatalogGroupUpdate
{
    name: string;
    icon: string;
    isHidden: boolean;
    products: ICatalogProductUpdate[];
    groups: ICatalogGroupUpdate[];
}

export interface ICatalogProductUpdate
{
    productId: Guid;
    formatId: Guid;
    status: CatalogProductStatus;
}

export interface IUpdateFullCatalog extends IBaseCommand<boolean>
{
    id: Guid;
    merchantId: Guid;
    root: ICatalogGroupUpdate;
}

export interface IUpdateProductFormat extends ICreateProductFormat
{
    id: Guid;
    price: number;
    name: string;
    value: number;
    unit: string;
}

export interface IUpdateProduct extends IBaseCommand<boolean>
{
    id: Guid;
    merchantId: Guid;
    name: string;
    description: string;
    color: string;
    formats: IUpdateProductFormat[];
}

export interface IOrdersReportResult
{
    rows: IOrdersReportRow[];
}

export interface IOrdersReportRow
{
    fromTime: Date | string;
    toTime: Date | string;
    groupId: unknown;
    groupName: string;
    total: number;
    min: number;
    max: number;
    avg: number;
    count: number;
}

export interface ITimeGroup
{
    unit: TimeUnit;
    value: number;
}

export enum TimeUnit
{
    Second = 0,
    Minute = 1,
    Hour = 2,
    Day = 3,
    Week = 4,
    Month = 5,
    Year = 6,
}

export interface IOrdersReport extends IBaseCommand<IOrdersReportResult>
{
    saleCircuitId: Guid;
    productId: Guid;
    salePointId: Guid;
    merchantId: Guid;
    orderType: OrderType;
    groupBy: string;
    groupByTime: ITimeGroup;
    fromDate: Date | string;
    toDate: Date | string;
}

export interface IWalletsReportResult
{
    rows: IWalletsReportRow[];
}

export interface IWalletsReportRow
{
    salePoint: IEntityView;
    totalCreated: number;
    totalActiveCards: number;
    totalActiveCardsWithBalance: number;
    totalTransactions: number;
    totalCharged: number;
    totalBuy: number;
    totalRefunded: number;
    totalReset: number;
    totalRemain: number;
    totalBalance: number;
}

export interface IWalletsReport extends IBaseCommand<IWalletsReportResult>
{
    saleCircuitId: Guid;
    salePointId: Guid;
    groupBy: string;
    fromDate: Date | string;
    toDate: Date | string;
    excludeGifts: boolean;
}

export interface IPrintCommand
{
    type: PrintCommandType;
    text: string;
    imageData: number[];
    fontSize: number;
    alignment: PrinterAlignment;
    textStyle: PrinterTextStyle;
    format: PrintImageFormat;
    height: number;
    width: number;
    rowSizeByte: number;
    linesToScroll: number;
}

export enum PrintCommandType
{
    PrintImage = 0,
    PrintText = 1,
    PrintQRCode = 2,
    ScrollPaper = 3,
}

export enum PrinterAlignment
{
    Left = 0,
    Center = 1,
    Right = 2,
}

export enum PrinterTextStyle
{
    Normal = 0,
    Bold = 1,
    Italic = 2,
    BoldItalic = 3,
}

export enum PrintImageFormat
{
    Bitmap = 0,
    Png = 1,
}

export interface ICreateReceiptTemplate extends IBaseCommand<Guid>
{
    saleCircuitId: Guid;
    templateName: string;
    language: string;
    commands: IPrintCommand[];
}

export enum SaleCircuitType
{
    Business = 1,
    Franchise = 2,
    Manifestation = 3,
    BusinessGroup = 4,
    Private = 5,
}

export interface ICreateSaleCircuit extends IBaseCommand<Guid>
{
    name: string;
    type: SaleCircuitType;
    address: IAddressData;
    currency: string;
    conversionRate: number;
    conversionCurrency: string;
}

export interface ISaleCircuitSnapshot
{
    id: Guid;
    saleCircuitId: Guid;
    time: Date | string;
    wallets: Record<Guid, ISnapshotWallet>;
    devices: Record<string, ISnapshotDevice>;
    transactions: Record<number, ISnapshotTransaction>;
    errors: string[];
    firstSequence: number;
    lastSequence: number;
}

export interface ISnapshotWallet
{
    id: Guid;
    type: WalletType;
    status: WalletStatus;
    stats: IWalletStats;
    cardCodes: string[];
    owner: IOwnerView;
    firstTransactionId: Guid;
    lastTransactionId: Guid;
    transactionCount: number;
    startBalance: number;
    endBalance: number;
    currentBalance: number;
    transactions: number[];
    errorCount: number;
}

export interface IWalletStats
{
    totalExpenses: number;
    totalIncome: number;
    totalRefund: number;
    totalIncomeBy: IWalletStatsChannels;
}

export interface IWalletStatsChannels
{
    cash: number;
    gift: number;
    online: number;
    bankCard: number;
}

export interface ISnapshotDevice
{
    id: Guid;
    keys: Record<string, string[]>;
    serial: string;
    type: DeviceType;
    info: IDeviceInfo;
}

export interface ISnapshotTransaction
{
    id: Guid;
    sequence: number;
    version: number;
    saleCircuitId: Guid;
    salePointId: Guid;
    prevTransactionId: Guid;
    type: TransactionType;
    requestTime: Date | string;
    executionTime: Date | string;
    orderId: Guid;
    ipAddress: string;
    deviceId: Guid;
    fromWalletId: Guid;
    toWalletId: Guid;
    provider: string;
    providerTransactionId: string;
    paymentCardId: Guid;
    paymentCardToken: string;
    paymentMethod: PaymentMethod;
    amount: number;
    signature: string;
    signatureKeyFingerprint: string;
    userId: Guid;
}

export enum TransactionType
{
    Transfer = 1,
    Buy = 2,
    OrderRefund = 3,
    CardRefund = 4,
    Charge = 5,
    Reset = 6,
}

export interface ICreateSnapshot extends IBaseCommand<ISaleCircuitSnapshot>
{
    saleCircuitId: Guid;
    startTime: Date | string;
    startSequence: number;
    includeTransactions: boolean;
    includeDevices: boolean;
    includeWallets: boolean;
    verifySignature: boolean;
    save: boolean;
}

export interface ICreateTransaction extends IBaseCommand<Guid>
{
    type: TransactionType;
    saleCircuitId: Guid;
    salePointId: Guid;
    orderId: Guid;
    fromWalletId: Guid;
    toWalletId: Guid;
    paymentCardId: Guid;
    provider: string;
    providerTransactionId: string;
    amount: number;
    paymentMethod: PaymentMethod;
    checkBalance: boolean;
    disableLock: boolean;
    paymentCardToken: string;
    connectionId: string;
    userId: Guid;
}

export interface IReceiptTemplateView
{
    commands: IPrintCommand[];
}

export interface IGetReceiptTemplate extends IBaseCommand<IReceiptTemplateView>
{
    saleCircuitId: Guid;
    templateName: string;
    language: string;
    args: Record<string, string>;
}

export interface ISaleCircuitOptions
{
    allowRefund: boolean;
    allowOnlineRecharge: boolean;
    allowCardTransfers: boolean;
    paymentType: PaymentCardType;
}

export interface IGetSaleCircuitOptions extends IBaseCommand<ISaleCircuitOptions>
{
    id: Guid;
}

export interface ISaleCircuitListView extends IEntityView
{
    merchants: ISaleCircuitMerchantListView[];
    salePoints: ISalePointListView[];
    id: Guid;
    name: string;
}

export interface ISaleCircuitMerchantListView
{
    role: MerchantRole;
    merchant: IEntityView;
}

export interface ISalePointListView extends IEntityView
{
    type: SalePointType;
    merchant: IEntityView;
    activeCatalog: IEntityView;
    address: IAddressData;
    status: SalePointStatus;
    id: Guid;
    name: string;
}

export enum SalePointStatus
{
    Active = 0,
    Closed = 1,
    Archived = 2,
}

export interface IListSaleCircuit extends IListCommand<ISaleCircuitListView>
{
    includeSalePoints: boolean;
    includeMerchants: boolean;
    pagination: IListPagination;
    sort: IListSort;
}

export interface ITransactionView
{
    id: Guid;
    saleCircuit: IEntityView;
    salePoint: IEntityView;
    merchant: IEntityView;
    orderId: Guid;
    paymentMethod: PaymentMethod;
    type: TransactionType;
    time: Date | string;
    amount: number;
    fromWallet: IWalletInfo;
    toWallet: IWalletInfo;
}

export interface IWalletInfo
{
    id: Guid;
    type: WalletType;
}

export interface IListTransactions extends IListCommand<ITransactionView>
{
    saleCircuitId: Guid;
    salePointId: Guid;
    ownerId: Guid;
    fromWalletId: Guid;
    toWalletId: Guid;
    orderId: Guid;
    transactionTypes: TransactionType[];
    fromDate: Date | string;
    toDate: Date | string;
    pagination: IListPagination;
    sort: IListSort;
}

export interface ISetSaleCircuitOptions extends IBaseCommand<boolean>
{
    id: Guid;
    options: ISaleCircuitOptions;
}

export interface IDeleteSalePoint extends IBaseCommand<boolean>
{
    id: Guid;
}

export interface IListSalePoint extends IListCommand<ISalePointListView>
{
    ids: Guid[];
    saleCircuitId: Guid;
    merchantId: Guid;
    status: SalePointStatus[];
    query: string;
    type: SalePointType[];
    pagination: IListPagination;
    sort: IListSort;
}

export interface ISetSalePointStatus extends IBaseCommand<boolean>
{
    id: Guid;
    status: SalePointStatus;
}

export interface IUpdateSalePoint extends IBaseCommand<boolean>
{
    id: Guid;
    saleCircuitId: Guid;
    name: string;
    address: IAddressData;
    type: SalePointType;
    activeCatalogId: Guid;
}

export interface IChangePassword extends IBaseCommand<boolean>
{
    userId: Guid;
    oldPassword: string;
    newPassword: string;
    deleteSessions: boolean;
}

export interface ICloseSession extends IBaseCommand<boolean>
{
    sessionId: Guid;
}

export interface ICreateAccessRequest extends IBaseCommand<Guid>
{
    expireTime: Date | string;
    friendlyName: string;
    permissions: string[];
    nonce: string;
    signature: string;
}

export interface IAccessToken
{
    userId: Guid;
    sessionId: Guid;
    walletId: Guid;
    cardId: Guid;
    operatorId: Guid;
    customerId: Guid;
    issuedByUserId: Guid;
    deviceId: Guid;
    actorType: ActorType;
    accountType: UserAccountType;
    expireTime: Date | string;
}

export enum ActorType
{
    User = 0,
    Device = 1,
    Wallet = 2,
    SysAdmin = 3,
    Anonymous = 4,
}

export enum UserAccountType
{
    Personal = 1,
    Business = 2,
    SysAdmin = 3,
}

export interface ICreateContextToken extends IBaseCommand<string>
{
    accessToken: IAccessToken;
}

export interface ICreateUser extends IBaseCommand<Guid>
{
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
    birthDate: Date | string;
    merchant: ICreateMerchant;
    accountType: UserAccountType;
    requireOtp: boolean;
}

export interface IPermissionsResult
{
    scopes: Record<string, string[]>;
    grants: string[];
}

export interface IGetPermissions extends IBaseCommand<IPermissionsResult>
{
}

export interface ISessionListView extends IEntityView
{
    device: IEntityView;
    deviceSerial: string;
    userAgent: string;
    lastIpAddress: string;
    status: UserSessionStatus;
    creationTime: Date | string;
    lastAccessTime: Date | string;
    expireTime: Date | string;
    userAgentInfo: IUserAgentInfo;
    nativeVersion: INativeAppVersion;
    webVersion: IWebAppVersion;
    id: Guid;
    name: string;
}

export enum UserSessionStatus
{
    Opened = 0,
    Closed = 1,
    Deleted = 2,
    Pending = 3,
}

export interface IUserAgentInfo
{
    browser: string;
    browserVersion: string;
    os: string;
    deviceType: string;
    osVersion: string;
}

export interface INativeAppVersion
{
    versionName: string;
    versionCode: number;
    packageName: string;
}

export interface IWebAppVersion
{
    versionName: string;
    environment: string;
}

export interface IListSession extends IListCommand<ISessionListView>
{
    name: string;
    browser: string;
    os: string;
    onlyActive: boolean;
    deviceId: Guid;
    pagination: IListPagination;
    sort: IListSort;
}

export interface IOpenSessionResult
{
    accessToken: string;
    sessionId: Guid;
    user: IUserInfo;
    deviceName: string;
    sessionStatus: UserSessionStatus;
}

export interface IUserInfo
{
    id: Guid;
    email: string;
    displayName: string;
    accountType: UserAccountType;
}

export interface IOpenSession extends IBaseCommand<IOpenSessionResult>
{
    username: string;
    password: string;
    deviceSerial: string;
    accessRequestId: Guid;
    walletId: Guid;
    neverExpires: boolean;
}

export interface IResetPassword extends IBaseCommand<boolean>
{
    token: string;
    password: string;
    deleteSessions: boolean;
}

export interface IResetPasswordRequest extends IBaseCommand<boolean>
{
    email: string;
}

export interface ISendOtp extends IBaseCommand<boolean>
{
}

export interface IValidateOtpResult
{
    sessionStatus: UserSessionStatus;
}

export interface IValidateOtp extends IBaseCommand<IValidateOtpResult>
{
    otp: string;
}

export interface IValidateSessionResult
{
    isValid: boolean;
}

export interface IValidateSession extends IBaseCommand<IValidateSessionResult>
{
    nativeVersion: INativeAppVersion;
    webVersion: IWebAppVersion;
}

export interface ICloseWallet extends IBaseCommand<boolean>
{
    salePointId: Guid;
    walletId: Guid;
}

export interface ICreateWallet extends IBaseCommand<Guid>
{
    saleCircuitId: Guid;
    salePointId: Guid;
    ownerId: Guid;
    cardCode: string;
    type: WalletType;
    expirationTime: Date | string;
}

export interface IWalletFullInfo extends IWalletInfo
{
    saleCircuitId: Guid;
    owner: IOwnerView;
    cardCodes: string[];
    status: WalletStatus;
    balance: number;
    isSecured: boolean;
    id: Guid;
    type: WalletType;
}

export interface IGetWalletInfo extends IBaseCommand<IWalletFullInfo>
{
    walletId: Guid;
}

export interface IProtectWallet extends IBaseCommand<boolean>
{
    walletId: Guid;
    ownerName: string;
    ownerEmail: string;
    ownerPhoneNumber: string;
    ownerPinCode: string;
}

export interface IRequestResetWalletPin extends IBaseCommand<boolean>
{
    walletId: Guid;
}

type CommandWithResult<TCommand, TResult> = {
    result: TResult;
    command: TCommand;
}
export interface CommandMap {
    "EndPairing": CommandWithResult<IEndPairing, string>,
    "ListCardReader": CommandWithResult<IListCardReader, IListResult<ICardReaderView>>,
    "RegisterCardReader": CommandWithResult<IRegisterCardReader, Guid>,
    "StartPairing": CommandWithResult<IStartPairing, string>,
    "WaitForCardRead": CommandWithResult<IWaitForCardRead, string>,
    "AuthorizeKey": CommandWithResult<IAuthorizeKey, boolean>,
    "CheckDeviceRegistration": CommandWithResult<ICheckDeviceRegistration, boolean>,
    "ListDevice": CommandWithResult<IListDevice, IListResult<IDeviceListView>>,
    "RegisterDevice": CommandWithResult<IRegisterDevice, Guid>,
    "GetSystemVersion": CommandWithResult<IGetSystemVersion, ISystemVersion>,
    "CreateMerchant": CommandWithResult<ICreateMerchant, Guid>,
    "CreateSalePoint": CommandWithResult<ICreateSalePoint, Guid>,
    "AddMerchant": CommandWithResult<IAddMerchant, Guid>,
    "ListMerchant": CommandWithResult<IListMerchant, IListResult<IMerchantListView>>,
    "ChangeOrder": CommandWithResult<IChangeOrder, boolean>,
    "CreateOrder": CommandWithResult<ICreateOrder, Guid>,
    "CreatePayment": CommandWithResult<ICreatePayment, string>,
    "GetOrder": CommandWithResult<IGetOrder, IOrderDetailsView>,
    "ListOrder": CommandWithResult<IListOrder, IListResult<IOrderListView>>,
    "PayOrder": CommandWithResult<IPayOrder, IPayResult>,
    "RefundOrder": CommandWithResult<IRefundOrder, boolean>,
    "AuthorizePayment": CommandWithResult<IAuthorizePayment, string>,
    "BlockCard": CommandWithResult<IBlockCard, IBlockCardResult>,
    "EncodeCardCode": CommandWithResult<IEncodeCardCode, string>,
    "GetCardInfo": CommandWithResult<IGetCardInfo, IPaymentCardInfo>,
    "InitCard": CommandWithResult<IInitCard, Guid>,
    "ListCard": CommandWithResult<IListCard, IListResult<ICardListView>>,
    "ReplaceCard": CommandWithResult<IReplaceCard, Guid>,
    "ResetCard": CommandWithResult<IResetCard, boolean>,
    "UnblockCard": CommandWithResult<IUnblockCard, boolean>,
    "CreateCatalog": CommandWithResult<ICreateCatalog, Guid>,
    "CreateProduct": CommandWithResult<ICreateProduct, Guid>,
    "DeleteCatalog": CommandWithResult<IDeleteCatalog, boolean>,
    "DeleteProduct": CommandWithResult<IDeleteProduct, boolean>,
    "GetFullCatalog": CommandWithResult<IGetFullCatalog, ICatalogFullView>,
    "ListCatalog": CommandWithResult<IListCatalog, IListResult<ICatalogListView>>,
    "ListProduct": CommandWithResult<IListProduct, IListResult<IProductListView>>,
    "UpdateFullCatalog": CommandWithResult<IUpdateFullCatalog, boolean>,
    "UpdateProduct": CommandWithResult<IUpdateProduct, boolean>,
    "OrdersReport": CommandWithResult<IOrdersReport, IOrdersReportResult>,
    "WalletsReport": CommandWithResult<IWalletsReport, IWalletsReportResult>,
    "CreateReceiptTemplate": CommandWithResult<ICreateReceiptTemplate, Guid>,
    "CreateSaleCircuit": CommandWithResult<ICreateSaleCircuit, Guid>,
    "CreateSnapshot": CommandWithResult<ICreateSnapshot, ISaleCircuitSnapshot>,
    "CreateTransaction": CommandWithResult<ICreateTransaction, Guid>,
    "GetReceiptTemplate": CommandWithResult<IGetReceiptTemplate, IReceiptTemplateView>,
    "GetSaleCircuitOptions": CommandWithResult<IGetSaleCircuitOptions, ISaleCircuitOptions>,
    "ListSaleCircuit": CommandWithResult<IListSaleCircuit, IListResult<ISaleCircuitListView>>,
    "ListTransactions": CommandWithResult<IListTransactions, IListResult<ITransactionView>>,
    "SetSaleCircuitOptions": CommandWithResult<ISetSaleCircuitOptions, boolean>,
    "DeleteSalePoint": CommandWithResult<IDeleteSalePoint, boolean>,
    "ListSalePoint": CommandWithResult<IListSalePoint, IListResult<ISalePointListView>>,
    "SetSalePointStatus": CommandWithResult<ISetSalePointStatus, boolean>,
    "UpdateSalePoint": CommandWithResult<IUpdateSalePoint, boolean>,
    "ChangePassword": CommandWithResult<IChangePassword, boolean>,
    "CloseSession": CommandWithResult<ICloseSession, boolean>,
    "CreateAccessRequest": CommandWithResult<ICreateAccessRequest, Guid>,
    "CreateContextToken": CommandWithResult<ICreateContextToken, string>,
    "CreateUser": CommandWithResult<ICreateUser, Guid>,
    "GetPermissions": CommandWithResult<IGetPermissions, IPermissionsResult>,
    "ListSession": CommandWithResult<IListSession, IListResult<ISessionListView>>,
    "OpenSession": CommandWithResult<IOpenSession, IOpenSessionResult>,
    "ResetPassword": CommandWithResult<IResetPassword, boolean>,
    "ResetPasswordRequest": CommandWithResult<IResetPasswordRequest, boolean>,
    "SendOtp": CommandWithResult<ISendOtp, boolean>,
    "ValidateOtp": CommandWithResult<IValidateOtp, IValidateOtpResult>,
    "ValidateSession": CommandWithResult<IValidateSession, IValidateSessionResult>,
    "CloseWallet": CommandWithResult<ICloseWallet, boolean>,
    "CreateWallet": CommandWithResult<ICreateWallet, Guid>,
    "GetWalletInfo": CommandWithResult<IGetWalletInfo, IWalletFullInfo>,
    "ProtectWallet": CommandWithResult<IProtectWallet, boolean>,
    "RequestResetWalletPin": CommandWithResult<IRequestResetWalletPin, boolean>,
}

export const CommandQueries = {
    "EndPairing": false,
    "ListCardReader": true,
    "RegisterCardReader": false,
    "StartPairing": false,
    "WaitForCardRead": false,
    "AuthorizeKey": false,
    "CheckDeviceRegistration": true,
    "ListDevice": true,
    "RegisterDevice": false,
    "GetSystemVersion": true,
    "CreateMerchant": false,
    "CreateSalePoint": false,
    "AddMerchant": false,
    "ListMerchant": true,
    "ChangeOrder": false,
    "CreateOrder": false,
    "CreatePayment": false,
    "GetOrder": true,
    "ListOrder": true,
    "PayOrder": false,
    "RefundOrder": false,
    "AuthorizePayment": false,
    "BlockCard": false,
    "EncodeCardCode": false,
    "GetCardInfo": true,
    "InitCard": false,
    "ListCard": true,
    "ReplaceCard": false,
    "ResetCard": false,
    "UnblockCard": false,
    "CreateCatalog": false,
    "CreateProduct": false,
    "DeleteCatalog": false,
    "DeleteProduct": false,
    "GetFullCatalog": true,
    "ListCatalog": true,
    "ListProduct": true,
    "UpdateFullCatalog": false,
    "UpdateProduct": false,
    "OrdersReport": true,
    "WalletsReport": true,
    "CreateReceiptTemplate": false,
    "CreateSaleCircuit": false,
    "CreateSnapshot": true,
    "CreateTransaction": false,
    "GetReceiptTemplate": true,
    "GetSaleCircuitOptions": true,
    "ListSaleCircuit": true,
    "ListTransactions": true,
    "SetSaleCircuitOptions": false,
    "DeleteSalePoint": false,
    "ListSalePoint": true,
    "SetSalePointStatus": false,
    "UpdateSalePoint": false,
    "ChangePassword": false,
    "CloseSession": false,
    "CreateAccessRequest": false,
    "CreateContextToken": false,
    "CreateUser": false,
    "GetPermissions": true,
    "ListSession": true,
    "OpenSession": false,
    "ResetPassword": false,
    "ResetPasswordRequest": false,
    "SendOtp": false,
    "ValidateOtp": false,
    "ValidateSession": true,
    "CloseWallet": false,
    "CreateWallet": false,
    "GetWalletInfo": true,
    "ProtectWallet": false,
    "RequestResetWalletPin": false,
}

